import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { TranslationModule } from '@lib/translation';
import { SnIconModule } from '@lib/ui/icon';

import { IFsLprProviderInterface } from '../../../../../api/interfaces';
import { VIClickOutsideDirective } from '../../../../../shared/directives';
import { ViLPRProvider } from '../../../../../shared/models';
import {
  buildProviderIssueState,
  getCameraModelSelectionKey,
  hasAxonVI,
  shouldSkipProvider,
  calculateTotalResults,
} from '../../helpers';
import { LPRCameraModelsSelection, LPRProviderSelection } from '../../models';
import { LPRSourceSelectService } from '../../services';
import { LPRSourceDropdownEntryComponent } from '../lpr-source-dropdown-entry/lpr-source-dropdown-entry.component';

/**
 * Expandable dropdown listing all available sources (Flock, All Axon, etc.)
 * Shows result counts, loading states, and error/warning indicators for each source
 * Calculates total results across selected sources, accounting for camera model filters
 */
@UntilDestroy()
@Component({
  selector: 'fs-lpr-source-dropdown',
  standalone: true,
  templateUrl: './lpr-source-dropdown.component.html',
  styleUrls: ['./lpr-source-dropdown.component.scss'],
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    SnIconModule,
    TranslationModule,
    VIClickOutsideDirective,
    LPRSourceDropdownEntryComponent,
  ],
})
export class LPRSourceDropdownComponent implements OnInit, OnDestroy {
  private static readonly MAX_TOTAL_RESULTS = 9999;

  @Input() providers$: Observable<IFsLprProviderInterface[]>;
  @Input() lprSourceSelectService: LPRSourceSelectService;
  @Input() shouldShowPartialMatches$: Observable<boolean>;
  @Input() isDisabled = false;

  readonly maxTotalResults = LPRSourceDropdownComponent.MAX_TOTAL_RESULTS;

  ViLPRProvider = ViLPRProvider;

  selectedSourcesCount$: Observable<number>;
  providersCount$: Observable<number>;
  totalResults$: Observable<number>;

  isSourcesDropdownVisible$ = new BehaviorSubject<boolean>(false);

  areProvidersEmpty$: Observable<boolean>;
  hasAllSourcesSelected$: Observable<boolean>;
  shouldShowWorksWithAxon$: Observable<boolean>;

  providerSelection$ = new ReplaySubject<LPRProviderSelection>(1);
  cameraModelSelection$ = new Subject<LPRCameraModelsSelection>();

  private sourcesSelection$: Observable<Map<ViLPRProvider, boolean>>;
  private cameraModelSelections$: Observable<Map<string, LPRCameraModelsSelection>>;

  @Output() providerSelectionChange = this.providerSelection$;
  @Output() cameraModelSelectionChange = this.cameraModelSelection$;

  ngOnInit(): void {
    this.setSourcesSelectionSubscription();
    this.setCameraModelSelectionsSubscription();
    this.setAreProvidersEmptySubscription();
    this.setProvidersCountSubscription();
    this.setSelectedSourcesCountSubscription();
    this.setHasAllSourcesSelectedSubscription();
    this.setTotalResultsSubscription();
    this.setShouldShowWorksWithAxonSubscription();
  }

  setSourcesDropdownVisibility(isVisible: boolean): void {
    this.isSourcesDropdownVisible$.next(isVisible);
  }

  isLastProvider(name: ViLPRProvider): Observable<boolean> {
    return this.providers$.pipe(
      map((providers) => providers[providers.length - 1]?.name === name),
      shareReplay(1),
    );
  }

  private setSourcesSelectionSubscription(): void {
    const initialMap = new Map<ViLPRProvider, boolean>();
    this.sourcesSelection$ = this.providerSelection$.pipe(
      scan((selectionMap, selection) => {
        selectionMap.set(selection.name, selection.isSelected);
        return new Map(selectionMap);
      }, initialMap),
      startWith(initialMap),
      shareReplay(1),
    );
  }

  private setCameraModelSelectionsSubscription(): void {
    const initialMap = new Map<string, LPRCameraModelsSelection>();
    this.cameraModelSelections$ = this.cameraModelSelection$.pipe(
      scan((selectionsMap, selection) => {
        const key = getCameraModelSelectionKey(selection.providerName, selection.cameraVendor);
        selectionsMap.set(key, selection);
        return new Map(selectionsMap);
      }, initialMap),
      startWith(initialMap),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.isSourcesDropdownVisible$.complete();
    this.providerSelection$.complete();
    this.cameraModelSelection$.complete();
  }

  private setAreProvidersEmptySubscription(): void {
    this.areProvidersEmpty$ = this.providers$.pipe(
      map((providers) => providers.length === 0),
      shareReplay(1),
    );
  }

  private setProvidersCountSubscription(): void {
    const worksWithAxonCount$ = this.lprSourceSelectService.worksWithAxonCount$ || of(0);
    this.providersCount$ = combineLatest([this.providers$, worksWithAxonCount$]).pipe(
      map(([providers, worksWithAxonCount]) => {
        const baseCount = providers.length;
        return hasAxonVI(providers) && worksWithAxonCount > 0 ? baseCount + 1 : baseCount;
      }),
    );
  }

  private setSelectedSourcesCountSubscription(): void {
    this.selectedSourcesCount$ = combineLatest([
      this.lprSourceSelectService.areAllProvidersLoaded$,
      this.sourcesSelection$,
      this.shouldShowPartialMatches$,
      this.providers$,
    ]).pipe(
      filter(([areAllProvidersLoaded]) => areAllProvidersLoaded),
      map(([, sourcesSelection, shouldShowPartialMatches, providers]) => {
        const providerIssueState = buildProviderIssueState(providers);
        let sourcesCount = 0;

        sourcesSelection.forEach((isSelected, providerName) => {
          if (!isSelected) return;

          if (!shouldSkipProvider(providerName, providerIssueState, shouldShowPartialMatches)) {
            sourcesCount += 1;
          }
        });

        return sourcesCount;
      }),
      shareReplay(1),
    );
  }

  private setHasAllSourcesSelectedSubscription(): void {
    this.hasAllSourcesSelected$ = this.selectedSourcesCount$.pipe(
      withLatestFrom(this.providersCount$),
      map(([selectedCount, providersCount]) => selectedCount === providersCount),
      shareReplay(1),
    );
  }

  private setTotalResultsSubscription(): void {
    const worksWithAxonCount$ = this.lprSourceSelectService.worksWithAxonCount$ || of(0);
    this.totalResults$ = combineLatest([
      this.lprSourceSelectService.areAllProvidersLoaded$,
      this.sourcesSelection$,
      this.cameraModelSelections$,
      this.shouldShowPartialMatches$,
      this.providers$,
      worksWithAxonCount$,
    ]).pipe(
      filter(([areAllProvidersLoaded]) => areAllProvidersLoaded),
      map(
        ([
          ,
          sourcesSelection,
          cameraModelSelections,
          shouldShowPartialMatches,
          providers,
          worksWithAxonCount,
        ]) =>
          calculateTotalResults({
            providers,
            sourcesSelection,
            cameraModelSelections,
            shouldShowPartialMatches,
            worksWithAxonCount,
          }),
      ),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private setShouldShowWorksWithAxonSubscription(): void {
    if (!this.lprSourceSelectService.worksWithAxonCount$) {
      this.shouldShowWorksWithAxon$ = of(false);
      return;
    }
    this.shouldShowWorksWithAxon$ = this.lprSourceSelectService.worksWithAxonCount$.pipe(
      map((count) => count > 0),
      shareReplay(1),
    );
  }
}
